import { useEffect, useRef } from 'react';

import { useAddInstConnectPage } from '../api';

import config from '@/config';
import { Departments } from '@/types';
import { toasts } from '@/utils/toasts';

const TOAST_ID = 'ig-connect-oauth';

const SCOPE = [
  'instagram_business_basic',
  'instagram_business_manage_messages',
  'instagram_business_manage_comments',
  'instagram_business_content_publish',
].join(',');

/**
 * Encapsulates the Instagram Business Login OAuth popup flow. Opening the popup
 * starts the authorization; the callback page posts the `code` back via
 * `postMessage`, which we exchange through `addInstConnectPage`. Re-running this
 * for an already-connected IG account refreshes its token in place (server-side
 * upsert by inst.id), so it doubles as the "reconnect" action.
 *
 * The one-time OAuth `code` must be exchanged exactly once. This hook is mounted
 * K times (add button + one per IG-connect card), so every instance would
 * otherwise handle the single callback message and fire K concurrent exchanges.
 * We gate on `isPendingRef`, set only by the instance that opened the popup, so
 * exactly one exchange runs.
 */
export function useIgConnectOAuth({ department }: { department?: Departments }) {
  const addInstConnectPage = useAddInstConnectPage();

  // Keep the latest mutate without re-registering the message listener each render.
  const mutateRef = useRef(addInstConnectPage.mutate);
  mutateRef.current = addInstConnectPage.mutate;

  // Keep the latest department without re-registering the message listener each render.
  const departmentRef = useRef(department);
  departmentRef.current = department;

  // True only between this instance opening the popup and receiving its callback.
  const isPendingRef = useRef(false);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data;
      if (!data || data.source !== 'ig-connect') return;
      // Ignore callbacks meant for another mounted instance's popup.
      if (!isPendingRef.current) return;
      isPendingRef.current = false;

      if (data.error || !data.code) {
        toasts.loadingToast.error(TOAST_ID, 'Не удалось подключить Instagram. Попробуйте ещё раз.');
        return;
      }

      toasts.loadingToast.loading(TOAST_ID, 'Подключение Instagram...');
      mutateRef.current(
        {
          code: data.code,
          redirectUri: config.instagramRedirectUri,
          ...(departmentRef.current ? { department: departmentRef.current } : {}),
        },
        {
          onSuccess: () => toasts.loadingToast.success(TOAST_ID, 'Instagram подключён'),
          onError: () =>
            toasts.loadingToast.error(
              TOAST_ID,
              'Не удалось подключить Instagram. Попробуйте ещё раз.'
            ),
        }
      );
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const open = () => {
    const authUrl =
      `https://api.instagram.com/oauth/authorize?client_id=${encodeURIComponent(config.instagramAppId)}` +
      `&redirect_uri=${encodeURIComponent(config.instagramRedirectUri)}` +
      `&scope=${encodeURIComponent(SCOPE)}&response_type=code`;
    const popup = window.open(authUrl, 'ig-connect-oauth', 'width=600,height=700');
    if (!popup) {
      toasts.loadingToast.error(
        TOAST_ID,
        'Всплывающее окно заблокировано. Разрешите popup для подключения Instagram.'
      );
      return;
    }
    isPendingRef.current = true;
  };

  return { open, isLoading: addInstConnectPage.isLoading };
}
